import { useMemo } from 'react';
import { format } from 'date-fns';
import { parseZonedDateTime, toZoned } from '@internationalized/date';
import { SWITCH_DURATION_LOWER } from '@lp/features/RoutePlanner/config';
import { isPrimaryHospital } from '@lp/features/RoutePlanner/utils/case-locations';
import { convertToIanaTimezone, getTzDateTimeString, zonedDateToString } from '@/utils/helpers';
import { AviaLegTypes, GroundLegTypes, SegmentTypeEnum } from '@/api/apis/route';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { MINUTE_IN_MS } from '@/constants/date.constants';

export const useDonorEta = (legs: (AviaLegTypes | GroundLegTypes)[] = []) => {
  const { donorHospitalDate, donorHospitalTime, donorHospitalTimeZone, donorHospitalDetails } = useBaseCaseDetails();
  const donorHospitalId = donorHospitalDetails?.id;

  const donorOrZoned = useMemo(() => {
    if (!donorHospitalDate || !donorHospitalTime || !donorHospitalTimeZone) return null;
    const formattedDonorOr = format(new Date(`${donorHospitalDate} ${donorHospitalTime}`), `yyyy-MM-dd'T'HH:mm:ss`);
    return parseZonedDateTime(`${formattedDonorOr}[${donorHospitalTimeZone}]`);
  }, [donorHospitalDate, donorHospitalTime, donorHospitalTimeZone]);

  const donorEtaZoned = useMemo(() => {
    if (!donorHospitalTimeZone) return null;

    const toDonorLeg = legs.find(
      (leg) =>
        isPrimaryHospital(leg.arrivalLocation, { donorHospitalId }) && leg.segmentType === SegmentTypeEnum.hospital,
    );

    if (!toDonorLeg) return null;

    return toZoned(
      parseZonedDateTime(getTzDateTimeString(toDonorLeg.arrivalTime)),
      convertToIanaTimezone(donorHospitalTimeZone),
    ).add({ milliseconds: SWITCH_DURATION_LOWER });
  }, [donorHospitalTimeZone, donorHospitalId, legs]);

  const donorEtaString = useMemo(() => {
    if (!donorEtaZoned) return '';

    return zonedDateToString(donorEtaZoned);
  }, [donorEtaZoned]);

  const offsetInMs = useMemo(() => {
    if (!donorEtaZoned || !donorOrZoned) return 0;

    const orUtc = donorOrZoned.toAbsoluteString();
    const etaUtc = donorEtaZoned?.toAbsoluteString();

    const offset = new Date(etaUtc).getTime() - new Date(orUtc).getTime();

    return offset > MINUTE_IN_MS ? offset : 0;
  }, [donorEtaZoned, donorOrZoned]);

  return { donorEtaString, offsetInMs };
};
